import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import {
  ActionIcon,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Select,
  Stack,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import BackButton from "../../../../components/View/BackButton";
import { editWebhook, findWebhook } from "../../../../api/ConfigsApi";

let headerId = 1;

const EditWebhook = ({ item, handleDone }) => {
  const lang = useLanguage((s) => s.language);
  const [opened, setOpened] = useState(false);
  const [finding, setFinding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState([{ key: "", value: "", id: 0 }]);

  const [itemProfile, setItemProfile] = useState(item);

  const eventOptions = useMemo(() => {
    return [
      {
        value: "email_open",
        label: lang.workflows.nodes.events.email_open,
      },
      {
        value: "link_click",
        label: lang.workflows.nodes.events.link_click,
      },
      {
        value: "email_answered",
        label: lang.workflows.nodes.events.email_answered,
      },
      {
        value: "linkedin_visited",
        label: lang.workflows.nodes.events.linkedin_visited,
      },
      {
        value: "linkedin_invitation_sent",
        label: lang.workflows.nodes.events.linkedin_invitation_sent,
      },
      {
        value: "linkedin_invitation_accepted",
        label: lang.workflows.nodes.events.linkedin_invitation_accepted,
      },
      {
        value: "linkedin_message_answered",
        label: lang.workflows.nodes.events.linkedin_message_answered,
      },
      {
        value: "linkedin_message_sent",
        label: lang.workflows.nodes.events.linkedin_message_sent,
      },
      {
        value: "sms_got",
        label: lang.workflows.nodes.events.sms_received,
      },
      {
        value: "sms_replied",
        label: lang.workflows.nodes.events.sms_replied,
      },
      {
        value: "all",
        label: lang.workflows.nodes.events.all,
      },
    ];
  }, [lang]);

  const handleOpen = () => {
    setOpened(true);
  };
  const handleClose = () => {
    setOpened(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    let formData = new FormData(e.target);
    let data = Object.fromEntries(formData);

    let headersAsObject = {};
    headers.forEach((item) => {
      if (item.value && item.key) {
        headersAsObject[item.key] = item.value;
      }
    });

    editWebhook({
      id: item.id,
      name: data?.name,
      url: data.url,
      enabled: true,
      event_type: data?.event,
      headers: headersAsObject,
    })
      .then((response) => {
        notifications.show({
          message: lang.webhooks.edited,
          color: "tertiary.8",
        });
        handleClose();
        handleDone();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (opened) {
      setFinding(true);
      findWebhook(item.id)
        .then((response) => {
          setItemProfile(response.data.webhook_profile);

          let h = response.data.webhook_profile.headers;
          if (Object.keys(h)?.length > 0) {
            setHeaders(
              Object.keys(h).map((k, i) => ({ key: k, value: h[k], id: i }))
            );
          } else {
            setHeaders([{ key: "", value: "", id: 0 }]);
          }
        })
        .finally(() => setFinding(false));
    }
  }, [opened, item.id]);

  // Headers
  const addHeader = () => {
    setHeaders((prev) => [...prev, { key: "", value: "", id: headerId++ }]);
  };

  const removeHeader = (id) => {
    setHeaders((prev) => {
      let newHeaders = [...prev];
      return newHeaders.filter((x) => x.id !== id);
    });
  };

  const editHeaderItem = (index, field, newValue) => {
    setHeaders((prev) => {
      let newHeaders = [...prev];
      newHeaders[index][field] = newValue;

      return newHeaders;
    });
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        leftSection={<IconEdit size={18} />}
        variant="light"
        ml={"auto"}
      >
        {lang.webhooks.edit}
      </Button>

      <Modal
        opened={opened}
        title={lang.webhooks.edit_title + item.name}
        onClose={handleClose}
        size={"50%"}
        keepMounted={false}
      >
        <Stack
          component={"form"}
          onSubmit={onSubmit}
          key={itemProfile?.arguments ? "a" : "b"}
          mih={300}
        >
          {finding ? (
            <LoadingOverlay visible={finding} />
          ) : (
            <>
              <Group wrap="nowrap">
                <TextInput
                  name="name"
                  defaultValue={itemProfile.name}
                  label={lang.webhooks.create.name}
                  placeholder={"My webhook"}
                  required
                  w={"100%"}
                  autoFocus
                  data-autofocus
                  size="md"
                />

                <Select
                  name="event"
                  label={lang.webhooks.create.event}
                  placeholder={lang.sync_zapier.event_label}
                  defaultValue={itemProfile.event_type}
                  required
                  data={eventOptions}
                  size="md"
                  w={"100%"}
                />
              </Group>

              <TextInput
                name="url"
                label={lang.sync_zapier.hook_url}
                defaultValue={itemProfile?.url}
                id="new-webhook-url"
                type="url"
                placeholder="https://your-link.com/hook"
                required
                size="md"
              />

              {headers.map((header, index) => (
                <Group key={header.id} align="end" w={"100%"} wrap="nowrap">
                  <TextInput
                    label={index === 0 ? "Header" : null}
                    placeholder="ex : Authorization"
                    w={"100%"}
                    defaultValue={header.key}
                    onChange={(e) =>
                      editHeaderItem(index, "key", e.target.value)
                    }
                    size="md"
                    pattern="^[a-zA-Z0-9'`#$%&*+.^_|~!-]+$"
                    required={headers.length > 1}
                    title={`${lang.webhooks.create.authorized_chars} ' # $ % & * + . ^ _ | ~ ! -`}
                  />
                  <TextInput
                    label={index === 0 ? lang.webhooks.value : null}
                    placeholder="ex : Bearer ..."
                    w={"100%"}
                    defaultValue={header.value}
                    onChange={(e) =>
                      editHeaderItem(index, "value", e.target.value)
                    }
                    size="md"
                    required={headers.length > 1}
                  />
                  {headers.length > 1 && (
                    <Tooltip label={lang.webhooks.create.delete_header}>
                      <ActionIcon
                        size={42}
                        color="red"
                        variant="light"
                        onClick={() => removeHeader(header.id)}
                      >
                        <IconTrash size={18} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <Tooltip label={lang.webhooks.create.add_header}>
                    <ActionIcon
                      size={42}
                      color="tertiary.8"
                      variant="light"
                      style={{
                        visibility:
                          index < headers.length - 1 ? "hidden" : "visible",
                      }}
                      onClick={addHeader}
                    >
                      <IconPlus size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ))}
            </>
          )}

          <Group justify="end" mt={finding ? "auto" : "lg"}>
            <BackButton onClick={handleClose} size="md" />
            <Button
              // disabled={
              //   type === "prm" ? !filter || filter?.values?.length === 0 : false
              // }
              leftSection={<IconEdit size={18} />}
              loading={loading}
              type="submit"
              size="md"
            >
              {lang.webhooks.edit}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default EditWebhook;
